import * as React from 'react'
import { Form } from '~/form-brain2'
import { getBaseErrorString } from '~/utils/formatters'
import { useTemplateFormBody, useUpdateEntity, useResetEntity } from '~/hooks'
import { experimentFormResolvers } from '~/features'
import {
  downloadsStepFormBody,
  downloadsStepSchema,
  hubStepFormBody,
  hubStepSchema,
  listStepFormBody,
  listStepSchema,
  pagedStepFormBody,
  pagedStepSchema
} from '~/features/worksheet/formHelpers'
import { CheckmarkIcon, FormLabel, SettingsContentWrapper, SettingsTitleRow } from '~/components/atoms'
import { BlurbBuilder, ButtonWithConfirm, ButtonWithMessage, FormRenderer, SubmitButtonComplex } from '~/components/molecules'

import type { EntityObject, EntitySchema, FormOnSubmitFn, FormStatus, SubscribeToFormStatusFn } from '~/form-brain2'
import type { SelectInput, ListOption, QuestionnaireFormat, TemplateSet, Template, AnyPlainOrInputElement } from '~/features'

interface Props {
  template: Template
  templateSet: TemplateSet
  formStatus: FormStatus | undefined
  allTemplates: Template[]
  subscribeToFormStatus: SubscribeToFormStatusFn
}

const
  formName = 'template-settings-form',
  schemaByFormat: Record<QuestionnaireFormat, EntitySchema> = {
    LIST: listStepSchema,
    PAGED: pagedStepSchema,
    HUB: hubStepSchema,
    DOWNLOADS: downloadsStepSchema
  },
  baseFormBodyByFormat: Record<QuestionnaireFormat, AnyPlainOrInputElement[]> = {
    LIST: listStepFormBody,
    PAGED: pagedStepFormBody,
    HUB: hubStepFormBody,
    DOWNLOADS: downloadsStepFormBody
  },
  TemplateSettingsForm: React.FC<Props> = ({ template, allTemplates, templateSet, formStatus, subscribeToFormStatus }) => {
    const
      { updateEntity, isUpdating } = useUpdateEntity(),
      { resetEntity, isResetting, errors: resetErrors, isSuccess: isResetSuccess } = useResetEntity({
        id: template.id,
        type: 'template',
        clearStateOnSuccessAfter: 4000
      }),
      { formBody } = useTemplateFormBody(baseFormBodyByFormat[template.format], templateSet.id),
      basedOnTitle = template.basedOnId
        ? (((formBody.find(o => o.id === 'stepBasedOnTemplate') as SelectInput | undefined)?.list ?? []) as ListOption[]).find(o => o.optionValue === template.basedOnId)?.optionLabel
        : null,
      onSubmit: FormOnSubmitFn = ({ values, submissionSource }) => updateEntity({
        newValues: values,
        entity: template,
        sourceOfUpdate: submissionSource
      }),
      resetStep: React.MouseEventHandler = (e) => {
        if (e) { e.preventDefault(); e.stopPropagation() }

        resetEntity()
      }

    return <SettingsContentWrapper>
      <SettingsTitleRow>
        <FormLabel
          label="Step Template Settings"
          showLabel
          asHeading
        />
        {!basedOnTitle
          ? null
          : <ButtonWithMessage
            messageText={isResetSuccess ? 'Step template has been reset' : undefined}
            icon={isResetSuccess ? <CheckmarkIcon /> : null}
            swapButtonAndMessage
            button={
              <ButtonWithConfirm
                onConfirm={resetStep}
                buttonProps={{
                  size: 'medium',
                  label: 'Reset',
                  color: 'caution',
                  isLoading: isResetting,
                  isDisabled: !!isUpdating || isResetSuccess
                }}
                hasSucceeded={isResetSuccess}
                cancelLabel="Cancel"
                confirmLabel="Confirm Reset"
                subheading="Reset step template"
                confirmationBody={`This will delete any changes you have made to the steo template and replace them with the settings and format from ${basedOnTitle}`}
                errorMessage={resetErrors ? getBaseErrorString(resetErrors, 'An unexpected error occurred') : undefined}
              />
            }
          />}
      </SettingsTitleRow>
      <BlurbBuilder
        iconContent="?"
        iconColor='hero'
        naturalWidth
        noWrapper
        withBackground
        paragraphs={[
          'These settings are available for teachers to override in individual projects. The values you set here are used as defaults.'
        ]}
      />
      <Form
        formId={formName}
        formBody={formBody}
        entitySchema={schemaByFormat[template.format]}
        initialValues={template as unknown as EntityObject}
        onSubmitCallback={onSubmit}
        FormRenderer={FormRenderer}
        subscribeToFormStatus={subscribeToFormStatus}
        formOptions={{
          automaticIdFields: true,
          templatesThisSet: allTemplates,
          RootFormRenderer: FormRenderer
        }}
        {...experimentFormResolvers}
      />
      <SubmitButtonComplex
        formId={formName}
        label="Save Changes"
        formStatus={formStatus}
        isLoading={isUpdating}
      />
    </SettingsContentWrapper>
  }

export default TemplateSettingsForm
